import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import { Destination, DESTINATION_TABLE_COLUMN, DestinationTableFilters } from '../types';
import { FILTER_IDS } from '../constants';

/**
 * Apply search filter to destinations
 */
export const applySearchFilter = (
  destinations: Destination[],
  searchQuery: string,
): Destination[] => {
  // Handle empty or invalid destinations array
  if (!destinations || !Array.isArray(destinations)) return [];
  if (!searchQuery || !searchQuery.trim()) return destinations;

  const lowercaseQuery = searchQuery.toLowerCase();
  return destinations.filter((destination) => {
    // Safely handle missing or invalid destination name
    if (!destination || typeof destination.name !== 'string') return false;
    return destination.name.toLowerCase().includes(lowercaseQuery);
  });
};

/**
 * Apply filters to destinations based on filter array
 */
export const applyFilters = (
  destinations: Destination[],
  filters: DestinationTableFilters,
): Destination[] => {
  // Handle empty or invalid destinations array
  if (!destinations || !Array.isArray(destinations)) return [];
  if (!filters || !Array.isArray(filters)) return destinations;

  // Check if showArchivedDestinations filter is active
  const showArchivedDestinations = filters.includes(FILTER_IDS.SHOW_ARCHIVED_DESTINATIONS);

  return destinations.filter((destination) => {
    // Skip invalid destinations
    if (!destination) return false;

    // Apply archived destinations filter
    if (showArchivedDestinations) {
      // Show all destinations (including archived)
      return true;
    } else {
      // Filter out archived destinations (show all except archived)
      return destination.status !== 'Archived';
    }
  });
};

/**
 * Sort destinations by specified column and direction
 */
export const applySorting = (
  destinations: Destination[],
  sortBy: DESTINATION_TABLE_COLUMN,
  sortDirection: SORT_DIRECTIONS,
): Destination[] => {
  // Handle empty or invalid destinations array
  if (!destinations || !Array.isArray(destinations)) return [];
  if (destinations.length === 0) return [];

  // Filter out null destinations before sorting
  const validDestinations = destinations.filter((destination) => destination != null);

  const direction = sortDirection === SORT_DIRECTIONS.ASCENDING ? 1 : -1;

  return [...validDestinations].sort((a, b) => {
    let valueA: string | number | undefined;
    let valueB: string | number | undefined;

    switch (sortBy) {
      case DESTINATION_TABLE_COLUMN.NAME:
        valueA = a?.name?.toLowerCase();
        valueB = b?.name?.toLowerCase();
        break;

      case DESTINATION_TABLE_COLUMN.STATUS:
        valueA = a.status;
        valueB = b.status;
        break;

      case DESTINATION_TABLE_COLUMN.LAST_SYNC:
        valueA = a.last_successful_transfer_ended_at
          ? new Date(a.last_successful_transfer_ended_at).getTime()
          : 0;
        valueB = b.last_successful_transfer_ended_at
          ? new Date(b.last_successful_transfer_ended_at).getTime()
          : 0;
        break;

      case DESTINATION_TABLE_COLUMN.LAST_MODIFIED:
        valueA = new Date(a.modifiedAt).getTime();
        valueB = new Date(b.modifiedAt).getTime();
        break;

      default:
        valueA = a.id;
        valueB = b.id;
    }

    // Handle undefined values for consistent sorting
    if (valueA === undefined && valueB === undefined) return 0;
    if (valueA === undefined) return 1 * direction;
    if (valueB === undefined) return -1 * direction;

    if (valueA < valueB) return -1 * direction;
    if (valueA > valueB) return 1 * direction;
    return 0;
  });
};

/**
 * Apply pagination to destinations
 */
export const applyPagination = (
  destinations: Destination[],
  currentPage: number,
  pageSize: number,
): Destination[] => {
  // Handle empty or invalid destinations array
  if (!destinations || !Array.isArray(destinations)) return [];
  if (destinations.length === 0) return [];

  // Handle invalid pagination parameters
  const safePage = Math.max(1, currentPage || 1);
  // Special handling for pageSize 0 - use 1 instead
  const safePageSize = pageSize === 0 ? 1 : Math.max(1, pageSize || 10);

  const startIndex = (safePage - 1) * safePageSize;
  const endIndex = startIndex + safePageSize;

  // Return empty array if start index is beyond array length
  if (startIndex >= destinations.length) return [];

  return destinations.slice(startIndex, endIndex);
};

/**
 * Calculate pagination info
 */
export const calculatePaginationInfo = (
  totalItems: number,
  currentPage: number,
  pageSize: number,
) => {
  // Handle invalid parameters
  const safeTotalItems = Math.max(0, totalItems || 0);
  const safePage = Math.max(1, currentPage || 1);
  const safePageSize = Math.max(1, pageSize || 10);

  const totalPages = Math.max(1, Math.ceil(safeTotalItems / safePageSize));
  const startItem = safeTotalItems > 0 ? (safePage - 1) * safePageSize + 1 : 0;
  const endItem = Math.min(safePage * safePageSize, safeTotalItems);

  return {
    totalPages,
    startItem,
    endItem,
    hasNextPage: safePage < totalPages,
    hasPreviousPage: safePage > 1,
  };
};
